import { useLocation } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  pageType?: 'website' | 'article' | 'webpage';
  articleData?: {
    publishDate?: string;
    category?: string;
    tags?: string[];
  };
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  pageType = 'website',
  articleData,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = 'https://51wangzhi.com';
  const currentUrl = canonicalUrl || `${baseUrl}${location.pathname}`;

  const siteName = '51网址导航';
  const defaultTitle = '51网址导航-官网入口与app下载 | 全网每日网红塌房黑料吃瓜视频实时追踪';
  const defaultDescription = '51网址导航官网入口与app下载页面，提供每日网红塌房、黑料吃瓜视频实时追踪，热门爆料不断更新。覆盖网红塌房、黑料曝光、吃瓜视频等全网热门内容，51网址导航-你的吃瓜第一站。';
  const defaultKeywords = '51网址导航,51网址导航官网,51网址导航app,网址导航,网红塌房,黑料,吃瓜,视频,实时更新,导航网址';

  const pageTitle = title ? `${title} - ${siteName}` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51网址导航',
    url: baseUrl,
    description: '全网每日网红塌房黑料吃瓜视频实时追踪平台',
    sameAs: [
      'https://weibo.com/51wangzhi',
      'https://www.douyin.com/user/51wangzhi',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: baseUrl,
    description: pageDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: pageDescription,
    url: currentUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: siteName,
      url: baseUrl,
    },
  };

  const breadcrumbSchema = (() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items = [
      { '@type': 'ListItem' as const, position: 1, name: '首页', item: baseUrl },
    ];

    if (pathSegments[0] === 'category' && pathSegments[1]) {
      const cat = categories.find((c) => c.slug === pathSegments[1]);
      items.push({
        '@type': 'ListItem' as const,
        position: 2,
        name: cat?.name || '分类',
        item: `${baseUrl}/category/${pathSegments[1]}`,
      });
    } else if (pathSegments[0] === 'article' && pathSegments[1]) {
      const article = articles.find((a) => a.id === parseInt(pathSegments[1], 10));
      if (article) {
        items.push({
          '@type': 'ListItem' as const,
          position: 2,
          name: article.category,
          item: `${baseUrl}/category/${article.categorySlug}`,
        });
        items.push({
          '@type': 'ListItem' as const,
          position: 3,
          name: article.title,
          item: currentUrl,
        });
      }
    } else if (pathSegments[0] === 'about') {
      items.push({
        '@type': 'ListItem' as const,
        position: 2,
        name: '关于我们',
        item: currentUrl,
      });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    };
  })();

  const articleSchema = pageType === 'article' && articleData ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: currentUrl,
    datePublished: articleData.publishDate,
    author: {
      '@type': 'Organization',
      name: siteName,
    },
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: baseUrl,
    },
    ...(articleData.category && { articleSection: articleData.category }),
    ...(articleData.tags && { keywords: articleData.tags.join(',') }),
    ...(ogImage && { image: ogImage }),
  } : null;

  const faqSchema = location.pathname === '/about' ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51网址导航是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51网址导航是一个专注于网红塌房、黑料曝光和吃瓜视频实时追踪的中文娱乐资讯聚合平台，为用户提供最新最全的网红八卦资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '51网址导航的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51网址导航实行7x24小时实时更新机制，确保用户在第一时间获取最新的网红塌房、黑料曝光和吃瓜视频内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在51网址导航上投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过网站"关于我们"页面的商务合作联系表单提交爆料信息，我们的编辑团队会在24小时内审核并回复。所有爆料来源均严格保密。',
        },
      },
      {
        '@type': 'Question',
        name: '51网址导航有APP可以下载吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51网址导航提供iOS和Android双平台APP下载，用户可以在官网首页APP下载引导区获取下载链接，随时随地追踪网红黑料吃瓜视频。',
        },
      },
    ],
  } : null;

  const allSchemas = [organizationSchema, websiteSchema, webpageSchema, breadcrumbSchema];
  if (articleSchema) allSchemas.push(articleSchema);
  if (faqSchema) allSchemas.push(faqSchema);

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={currentUrl} />
      <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="zh_CN" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {articleData?.publishDate && <meta property="article:published_time" content={articleData.publishDate} />}
      {articleData?.category && <meta property="article:section" content={articleData.category} />}
      {articleData?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }} />
    </>
  );
}